import { RotateCcw } from 'lucide-react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Button } from '../../../../ui/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';
import { Textarea } from '../../../../ui/components/ui/textarea';
import { BillingTerm } from '../../../products/constants/billing-term.enum';
import { formatTermMonths } from '../../../products/lib/billing-term';
import { centsToEuroInput, formatCents, parseEuroInputToCents } from '../../../products/lib/money';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { isBillingTerm } from '../../lib/enum-guards';
import { OfferProductOptionResponseDto, OfferProductResponseDto } from '../../types/response';
import OfferFormDisclosure from '../offer-form-disclosure/offer-form-disclosure';
import OfferFormSection, { CollapsiblePanelProps } from '../offer-form-section/offer-form-section';
import { OfferFormValues } from '../offer-form/offer-form.schema';

type ProductTextField = 'productId' | 'title' | 'description' | 'monthlyPrice' | 'additionalConditions';

const findPrice = (product: OfferProductOptionResponseDto | undefined, term: BillingTerm | undefined) =>
  term ? product?.prices.find((price) => price.term === term) : undefined;

export interface OfferProductSectionProps {
  form: UseFormReturn<OfferFormValues>;
  billingTerms: BillingTermMetaResponseDto[];
  /** Active products only. */
  products: OfferProductOptionResponseDto[];
  panel: CollapsiblePanelProps;
  /** Title, description, price and conditions – collapsed by default. */
  detailsPanel: CollapsiblePanelProps;
  /** On a correction: the contract keeps its capacity as long as product and term stay. */
  contractProduct?: OfferProductResponseDto;
}

/** Term first, then a product with a price for it; the product only fills the editable fields as a template. */
const OfferProductSection = ({ form, billingTerms, products, panel, detailsPanel, contractProduct }: OfferProductSectionProps) => {
  const { control, setValue, formState } = form;
  const term = useWatch({ control, name: 'term' });
  const productId = useWatch({ control, name: 'productId' });
  const monthlyPrice = useWatch({ control, name: 'monthlyPrice' });

  const availableProducts = term ? products.filter((product) => !!findPrice(product, term)) : [];
  const selectedProduct = products.find((product) => product._id === productId);
  const templatePrice = findPrice(selectedProduct, term);
  const isContractProduct = contractProduct?.productId === productId && contractProduct?.term === term;
  const capacity = isContractProduct && contractProduct ? contractProduct.capacity : selectedProduct?.capacity;
  const previewCents = parseEuroInputToCents(monthlyPrice);

  // Re-validate programmatic changes only once the user has tried to submit, like typed input.
  const fieldOptions = { shouldDirty: true, shouldValidate: formState.isSubmitted };
  const setField = (name: ProductTextField, value: string) => setValue(name, value, fieldOptions);

  const loadTemplate = (product: OfferProductOptionResponseDto, nextTerm: BillingTerm) => {
    const price = findPrice(product, nextTerm);
    setField('productId', product._id);
    setField('title', product.title);
    setField('description', product.description);
    setField('monthlyPrice', price ? centsToEuroInput(price.monthlyPriceCents) : '');
    setField('additionalConditions', price?.additionalConditions ?? '');
  };

  const clearProduct = () => {
    setField('productId', '');
    setField('title', '');
    setField('description', '');
    setField('monthlyPrice', '');
    setField('additionalConditions', '');
  };

  // Price and conditions belong to the term, so they follow it; title and description keep the admin's edits.
  const handleTermChange = (value: string) => {
    if (!isBillingTerm(value) || value === term) return;
    setValue('term', value, fieldOptions);
    if (!selectedProduct) return;
    const price = findPrice(selectedProduct, value);
    if (!price) {
      clearProduct();
      return;
    }
    setField('monthlyPrice', centsToEuroInput(price.monthlyPriceCents));
    setField('additionalConditions', price.additionalConditions ?? '');
  };

  const handleProductChange = (value: string) => {
    const product = products.find((p) => p._id === value);
    if (product && term) loadTemplate(product, term);
  };

  return (
    <OfferFormSection title="Produkt" description="Erst die Laufzeit wählen, dann ein Produkt mit Preis für diese Laufzeit." panel={panel}>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="term"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">Laufzeit *</FormLabel>
              <Select value={field.value ?? ''} onValueChange={handleTermChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Laufzeit wählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {billingTerms.map(({ term: value, months }) => (
                    <SelectItem key={value} value={value}>
                      {formatTermMonths(months)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="productId"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">Produkt *</FormLabel>
              <Select value={field.value} onValueChange={handleProductChange} disabled={!term || availableProducts.length === 0}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !term ? 'Laufzeit benötigt…' : availableProducts.length === 0 ? 'Kein Produkt für diese Laufzeit' : 'Produkt wählen'
                      }
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product._id} value={product._id}>
                      {product.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {selectedProduct && term && (
        <>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] px-4 py-3">
            <div className="flex flex-col">
              <span className="text-[14px] font-bold text-[#12282A]">
                Kapazität: {capacity} {capacity === 1 ? 'Slot' : 'Slots'}
              </span>
              <span className="text-[12px] text-[#888]">Nicht anpassbar.</span>
            </div>
            <Button type="button" variant="outline" size="sm" className="flex gap-2 shrink-0" onClick={() => loadTemplate(selectedProduct, term)}>
              <RotateCcw className="h-4 w-4" />
              Produktdaten neu laden
            </Button>
          </div>

          <OfferFormDisclosure title="Produktdetails" hint="(nur abändern bei abweichenden Konditionen)" panel={detailsPanel}>
            <FormField
              control={control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Titel *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Einfacher Text, Zeilenumbrüche bleiben erhalten" className="min-h-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="monthlyPrice"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Preis netto pro Monat in € *</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="decimal" placeholder="z. B. 499,00" className="w-48" />
                  </FormControl>
                  {previewCents !== null && (
                    <p className="text-[12px] text-[#888]">
                      {formatCents(previewCents)} netto / Monat
                      {templatePrice &&
                        templatePrice.monthlyPriceCents !== previewCents &&
                        ` · Produktpreis: ${formatCents(templatePrice.monthlyPriceCents)}`}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="additionalConditions"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Zusätzliche Konditionen</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="optional, z. B. einmalige Set-Up-Gebühr oder Rabatt" className="min-h-16" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </OfferFormDisclosure>
        </>
      )}
    </OfferFormSection>
  );
};

export default OfferProductSection;
